import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-[#070709] border border-[#232530] px-4 py-2.5 text-[#ede8e1] font-light placeholder:text-[#57534e] focus:border-[#581313] focus:outline-none transition-colors text-sm";

export const SectionsTab = ({ onChanged }) => {
  const [sections, setSections] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newLayout, setNewLayout] = useState("grid");

  const load = async () => {
    const { data } = await api.get("/admin/sections");
    setSections(data);
  };

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []);

  const update = async (s, patch) => {
    try {
      await api.put(`/admin/sections/${s.id}`, { title: s.title, ...patch });
      await load();
      onChanged?.().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const rename = async (s) => {
    const title = window.prompt("Rename the section:", s.title);
    if (title && title !== s.title) {
      await update(s, { title });
      toast.success("Section renamed.");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Destroy "${s.title}" and every entry within it?`)) return;
    try {
      await api.delete(`/admin/sections/${s.id}`);
      toast.success("Section destroyed.");
      await load();
      onChanged?.().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const a = sections[idx], b = sections[j];
    await update(a, { order: b.order });
    await update(b, { order: a.order });
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await api.post("/admin/sections", { title: newTitle.trim(), layout: newLayout });
      toast.success("A new chamber opens in the Vault.");
      setNewTitle("");
      await load();
      onChanged?.().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="cms-sections-list">
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-4 mb-8">
        <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Name a new section — Research, Reading List, Exhibitions…"
          className={`${inputCls} flex-1`} data-testid="cms-new-section-input" />
        <select value={newLayout} onChange={(e) => setNewLayout(e.target.value)} className={`${inputCls} sm:w-44`} data-testid="cms-new-section-layout">
          <option value="grid">Grid</option>
          <option value="bento">Bento</option>
          <option value="list">List</option>
          <option value="mosaic">Mosaic</option>
        </select>
        <button type="submit" data-testid="cms-add-section-button"
          className="flex items-center justify-center gap-2 bg-[#581313] hover:bg-[#581313] text-[#ede8e1] border border-[#581313]/40 font-mono text-xs uppercase tracking-widest px-6 py-2.5 transition-colors">
          <Plus size={14} /> Add Section
        </button>
      </form>
      <div className="border border-[#232530] divide-y divide-[#232530]">
        {sections.map((s, i) => (
          <div key={s.id} className="flex items-center gap-3 px-5 py-4 bg-[#0d0e12]" data-testid={`cms-section-row-${s.slug}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} className="text-[#57534e] hover:text-[#ede8e1] transition-colors" data-testid={`cms-section-up-${s.slug}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} className="text-[#57534e] hover:text-[#ede8e1] transition-colors" data-testid={`cms-section-down-${s.slug}`}><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <button onClick={() => rename(s)} className="font-serif text-lg text-[#ede8e1] hover:text-[#581313] transition-colors text-left" data-testid={`cms-section-rename-${s.slug}`}>
                {s.title}
              </button>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c] mt-0.5">/{s.slug} — {s.layout}</p>
            </div>
            <button onClick={() => update(s, { visible: !s.visible })} data-testid={`cms-section-toggle-${s.slug}`}
              className={`transition-colors ${s.visible ? "text-[#581313]" : "text-[#57534e]"}`}
              title={s.visible ? "Visible — click to hide" : "Hidden — click to show"}>
              {s.visible ? <Eye size={17} /> : <EyeOff size={17} />}
            </button>
            <button onClick={() => remove(s)} className="text-[#a8a29e] hover:text-[#581313] transition-colors" data-testid={`cms-section-delete-${s.slug}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[#57534e]">
        Click a name to rename it. Hidden sections vanish from the public site but keep their entries.
      </p>
    </div>
  );
};
